import { makeObservable, observable, computed, action } from "mobx";

import type { Nullable } from "models-webgl2";
import { EventEmitter } from "utils/helpers/classes/event-emitter";
import { Vec2 } from "utils/math/vec2";
import { clamp } from "utils/math/clamp";
import { findClosestNumbers } from "utils/math/find-closest-numbers";
import { CommandController } from "utils/helpers/classes/command-controller";
import { makeAutoBindFunctions } from "utils/helpers/make-auto-bind-functions";
import { deepObserve } from "utils/mobx/deep-observe";
import { Camera2d, CAMERA_MAX_SCALE, cameraAnimationTime, cameraScalesArr } from "../camera-2d";
import {
  Webgl2Component,
  ChessGridComponent,
  LineGridComponent,
  MeasurementComponent,
  SelectionComponent,
  CanvasResizeSelectionComponent,
  CropSelectionComponent,
  MeasurementSelectionComponent,
} from "../webgl2-component";
import { Webgl2 } from "../webgl2";
import { EventsManager } from "./events-manager";
import { Global } from "./global";
import { MousePicking } from "./mouse-picking";
import { RendererExport } from "./renderer-export";
import {
  CanvasResizeTool,
  CropTool,
  EraseTool,
  HandSelectionTool,
  HandTool,
  MeasurementTool,
  RetouchTool,
  SelectionTool,
} from "./tools";
import { maxCanvasSize, minCanvasSize } from "./webgl2d-renderer.constants";
import {
  Webgl2dRendererParentType,
  Webgl2dRendererTools,
  Webgl2dRendererEmitter,
} from "./webgl2d-renderer.types";

export class Webgl2dRenderer {
  public parent: Webgl2dRendererParentType;
  private _parentObserver: ResizeObserver | null = null;

  public readonly canvas: HTMLCanvasElement;
  public readonly ctx: CanvasRenderingContext2D;

  public readonly eventsManager: EventsManager;
  public readonly export: RendererExport;
  private readonly _mousePicking: MousePicking;

  protected readonly _emitter = new EventEmitter<Webgl2dRendererEmitter>();

  public get events() {
    return this._emitter.events;
  }

  public readonly camera: Camera2d;

  public readonly tools: Webgl2dRendererTools = {} as any;

  public commands = new CommandController();

  private _mounted = false;
  private _animationFrameId = 0;

  private _rulersEnabled = false;
  private _rulersHoverScreenPos: Vec2 | null = null;

  public chessGridComponent: ChessGridComponent;
  private _lineGridComponent: LineGridComponent;

  public selectionComponent: SelectionComponent;
  public canvasResizeSelectionComponent: CanvasResizeSelectionComponent;
  public cropSelectionComponent: CropSelectionComponent;
  public measurementSelectionComponent: MeasurementSelectionComponent;

  public components: Webgl2Component[] = [];

  @observable
  public selectedComponent: Webgl2Component | null | undefined = null;

  @computed
  public get hasSelectedComponent() {
    return !!this.selectedComponent;
  }

  @observable
  public canvasTranslation: Vec2 = Vec2.zero();

  @observable
  public canvasSize: Vec2 = Vec2.zero();

  @observable
  public isLogoWatermarkEdit = false;

  @computed
  public get canvasGlobalCenter(): Vec2 {
    const offsetVec = this.canvasTranslation.clone().add(this.canvasSize);
    return this.canvasTranslation.median(offsetVec);
  }

  private _scissorsOffset: Vec2 = Vec2.zero();
  private _scissorsSize: Vec2 = Vec2.zero();

  private _disposeMap = new Map<Webgl2Component, VoidFunction>();

  public static global = Global;

  constructor() {
    this.canvas = document.createElement("canvas");
    // NOTE: since we have checks for webgl2, it's 100% guaranteed that we will have "2d" context
    this.ctx = this.canvas.getContext("2d") as CanvasRenderingContext2D;

    this.camera = new Camera2d();
    this.camera.events.on("change", () => {
      this._calcScissors();
      this.render();
    });

    this.camera.setTranslationMapCallback((translation) => {
      const offset = CAMERA_MAX_SCALE / this.camera.scale;

      translation.clamp(
        this.camera.projection
          .divide(this.camera.scale)
          .negate()
          .add(offset, offset)
          .add(this.canvasTranslation),
        this.canvasSize.clone().sub(offset, offset).add(this.canvasTranslation),
      );

      return translation;
    });

    this.eventsManager = new EventsManager(this);
    this.export = new RendererExport(this);
    this._mousePicking = new MousePicking(this);

    makeObservable(this);
    makeAutoBindFunctions(this);
  }

  public setViewport(vec: Vec2): void;
  public setViewport(width: number, height: number): void;
  public setViewport(a: any, b?: any): void {
    const w = typeof a === "number" ? a : a.width;
    const h = typeof b === "number" ? b : a.height;

    if (this.camera.projection.equals(w, h)) return;

    this.canvas.width = w;
    this.canvas.height = h;
    this.camera.updateProjection(w, h);

    this.zoomToCenter();

    this.render();
  }

  public setCanvasTranslate(vec: Vec2, autoZoom?: boolean): void;
  public setCanvasTranslate(width: number, height: number, autoZoom?: boolean): void;
  public setCanvasTranslate(a: any, b?: any, c = false): void {
    const autoZoom = c || (typeof b === "boolean" ? b : false);
    const x = typeof a === "number" ? a : a.x;
    const y = typeof b === "number" ? b : a.y;

    this.chessGridComponent.transform.setTranslation(x, y);
    this._lineGridComponent.transform.setTranslation(x, y);
    this.canvasTranslation.set(x, y);

    this.canvasResizeSelectionComponent.copyComponentTransform(this.chessGridComponent);

    if (autoZoom) this.zoomToCenter();

    this._calcScissors();

    this._emitter.emit("canvasTransform");

    this.render();
  }

  public setCanvasSize(vec: Vec2, autoZoom?: boolean): void;
  public setCanvasSize(width: number, height: number, autoZoom?: boolean): void;
  public setCanvasSize(a: any, b?: any, c = false): void {
    const autoZoom = c || (typeof b === "boolean" ? b : false);
    const w = clamp(typeof a === "number" ? a : a.width, minCanvasSize, maxCanvasSize);
    const h = clamp(typeof b === "number" ? b : a.height, minCanvasSize, maxCanvasSize);

    this.chessGridComponent.transform.setSize(w, h);
    this._lineGridComponent.transform.setSize(w, h);
    this.canvasSize.set(w, h);

    this.canvasResizeSelectionComponent.copyComponentTransform(this.chessGridComponent);

    if (autoZoom) this.zoomToCenter();

    this._calcScissors();

    this._emitter.emit("canvasTransform");

    this.render();
  }

  public zoomToCenter() {
    if (this.canvasSize.getFloor().hasZero()) return;

    const viewport = this.camera.projection;
    const rulerThickness = this._getRulerThicknessPx(viewport);
    const contentViewport = viewport.clone().sub(rulerThickness, rulerThickness);
    if (contentViewport.getFloor().hasZero()) return;

    const [min] = findClosestNumbers(
      contentViewport.divide(this.canvasSize).min(),
      ...cameraScalesArr.map(([, scale]) => scale),
    );

    this.camera.setScale(min);
    this.recenterViewport();
  }

  public zoomToCenterSmooth(ms = cameraAnimationTime * 2) {
    if (this.canvasSize.getFloor().hasZero()) return;

    const viewport = this.camera.projection;
    const rulerThickness = this._getRulerThicknessPx(viewport);
    const contentViewport = viewport.clone().sub(rulerThickness, rulerThickness);
    if (contentViewport.getFloor().hasZero()) return;

    const [min] = findClosestNumbers(
      contentViewport.divide(this.canvasSize).min(),
      ...cameraScalesArr.map(([, scale]) => scale),
    );

    const targetCenter = this.canvasGlobalCenter.clone();
    if (rulerThickness) {
      const scale = min || 1;
      const centerOffset = rulerThickness / (2 * scale);
      targetCenter.sub(centerOffset, centerOffset);
    }

    this.camera.animateCenterViewportOnGlobalPosition(targetCenter, min, ms);
  }

  public mount(parent: Webgl2dRendererParentType, width: number, height: number): boolean;
  public mount(parent: Webgl2dRendererParentType, vec: Vec2): boolean;
  public mount(parent: Webgl2dRendererParentType, a: any, b?: any): boolean {
    this.unmount();

    if (!parent) return false;

    const isVec = a instanceof Vec2;
    const w = isVec ? a.x : a;
    const h = isVec ? a.y : b;

    this.parent = parent;
    this._mounted = true;
    this.eventsManager.registerListeners();
    this._mousePicking.prepare();

    const observer = new ResizeObserver(([entry]) => {
      // https://webgl2fundamentals.org/webgl/lessons/webgl-resizing-the-canvas.html
      let width: number;
      let height: number;
      let dpr = window.devicePixelRatio;
      if (entry.devicePixelContentBoxSize) {
        // NOTE: Only this path gives the correct answer
        // The other 2 paths are an imperfect fallback
        // for browsers that don't provide anyway to do this
        width = entry.devicePixelContentBoxSize[0].inlineSize;
        height = entry.devicePixelContentBoxSize[0].blockSize;
        dpr = 1; // it's already in width and height
      } else if (entry.contentBoxSize) {
        width = entry.contentBoxSize[0].inlineSize;
        height = entry.contentBoxSize[0].blockSize;
      } else {
        // legacy
        width = entry.contentRect.width;
        height = entry.contentRect.height;
      }
      const displayWidth = Math.round(width * dpr);
      const displayHeight = Math.round(height * dpr);
      this.setViewport(displayWidth, displayHeight);
    });
    observer.observe(parent, { box: "content-box" });
    this._parentObserver = observer;

    this._prepareComponents();
    this._prepareTools();
    this.eventsManager.assignTool(this.tools.handSelectionTool);

    this.setCanvasSize(w, h);
    this.setViewport(
      Math.round(parent.clientWidth * window.devicePixelRatio),
      Math.round(parent.clientHeight * window.devicePixelRatio),
    );

    this.canvas.tabIndex = 0;
    this.canvas.style.outline = "none";
    parent.appendChild(this.canvas);

    this.render();

    return true;
  }

  public unmount() {
    this.canvas?.remove();
    this.parent = null;
    this._parentObserver?.disconnect();
    this._parentObserver = null;
    this.eventsManager.unregisterListeners();
    window.cancelAnimationFrame(this._animationFrameId);
    this._disposeMap.forEach((dispose) => dispose());
    this._mounted = false;
  }

  public addComponent(component: Webgl2Component<any, any>): boolean {
    const canObserve = this._observeComponent(component);
    if (!canObserve) return false;
    this.components.push(component);
    this.render();
    return true;
  }

  public removeComponent(component: Webgl2Component<any, any>): boolean {
    const foundIndex = this.components.findIndex((c) => c === component);
    if (foundIndex === -1 || !this._disposeComponent(component)) return false;
    this.components.splice(foundIndex, 1);
    this.render();
    return true;
  }

  public findComponent(component: Nullable<Webgl2Component>): Nullable<Webgl2Component> {
    return this.components.find((c) => c === component);
  }

  public findComponentUnderMouse(e: MouseEvent | TouchEvent): Nullable<Webgl2Component> {
    const id = this._mousePicking.getShapeId(e);
    return Webgl2Component.components[id];
  }

  @action
  public selectComponent(component: Webgl2Component<any, any> | null | undefined) {
    if (!component) return this.deselectComponent();
    this.selectedComponent = component;
    this.selectionComponent.copyComponentTransform(component);
    this.render();
  }

  @action
  public deselectComponent() {
    if (!this.hasSelectedComponent) return;
    this.selectedComponent = null;
    this.render();
  }

  public renderComponents() {
    for (const component of this.components) {
      Webgl2.renderComponent(component);
    }
  }

  public render() {
    if (this._animationFrameId || !this._mounted) return;
    this._animationFrameId = window.requestAnimationFrame(this._render);
  }

  public get rulersEnabled() {
    return this._rulersEnabled;
  }

  public setRulersEnabled(enabled: boolean) {
    if (this._rulersEnabled === enabled) return;
    this._rulersEnabled = enabled;
    if (!enabled) this.setRulersHoverScreenPosition(null);
    // Re-fit content area when toggling rulers on/off.
    // When rulers are enabled, we fit to the reduced content viewport (viewport - thickness).
    // When rulers are disabled, we fit back to the full viewport to avoid excessive margins.
    this.zoomToCenter();
    this.render();
  }

  public setRulersHoverScreenPosition(pos: Vec2 | null) {
    if (!pos) {
      if (!this._rulersHoverScreenPos) return;
      this._rulersHoverScreenPos = null;
      this.render();
      return;
    }

    if (!this._rulersEnabled) return;

    if (!this._rulersHoverScreenPos) {
      this._rulersHoverScreenPos = pos.clone();
      this.render();
      return;
    }

    const changed = this._rulersHoverScreenPos.copy(pos);
    if (changed) this.render();
  }

  private _render() {
    this._animationFrameId = 0;

    if (this.camera.projection.getFloor().hasZero()) return;

    const gl = Webgl2.gl;

    // Setup block
    const viewport = this.camera.projection;
    Webgl2.setViewport(viewport);
    Webgl2.camera.copy(this.camera);
    Webgl2.clear();

    gl.enable(gl.BLEND);
    gl.blendFuncSeparate(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA, gl.ONE, gl.ONE_MINUS_SRC_ALPHA);

    // set the scissor rectangle
    gl.enable(gl.SCISSOR_TEST);
    gl.scissor(
      this._scissorsOffset.x,
      this._scissorsOffset.y,
      this._scissorsSize.width,
      this._scissorsSize.height,
    );

    // Render block
    Webgl2.renderComponent(this.chessGridComponent);

    this.renderComponents();

    Webgl2.renderComponent(this._lineGridComponent, { cameraScale: this.camera.scale });

    gl.disable(gl.SCISSOR_TEST);

    if (this.eventsManager.checkForTool(this.tools.cropTool)) {
      Webgl2.renderComponent(this.cropSelectionComponent);
    } else if (this.eventsManager.checkForTool(this.tools.canvasResizeTool)) {
      Webgl2.renderComponent(this.canvasResizeSelectionComponent);
    } else {
      if (this.selectedComponent instanceof MeasurementComponent) {
        Webgl2.renderComponent(this.measurementSelectionComponent);
      } else {
        Webgl2.renderComponent(this.selectionComponent);
      }
    }

    gl.disable(gl.BLEND);

    // Render result to ui canvas
    this.ctx.clearRect(0, 0, viewport.width, viewport.height);
    this.ctx.drawImage(Webgl2.canvas, 0, 0);
    if (this._rulersEnabled) {
      const dpr = window.devicePixelRatio || 1;
      const thickness = Math.round(28 * dpr);
      const rulerThickness = Math.min(thickness, viewport.width, viewport.height);

      // Reserve space for rulers so they don't overlap the scene.
      // We intentionally clear these strips after drawing WebGL output, but before drawing rulers.
      this.ctx.clearRect(0, 0, viewport.width, rulerThickness);
      this.ctx.clearRect(0, 0, rulerThickness, viewport.height);
    }
    this._drawRulers(viewport);
    this._emitter.emit("render");
  }

  private _drawRulers(viewport: Vec2) {
    if (!this._rulersEnabled) return;
    if (this.canvasSize.getFloor().hasZero()) return;

    const scale = this.camera.scale || 1;
    if (!scale) return;

    const dpr = window.devicePixelRatio || 1;
    const thickness = Math.round(28 * dpr);
    const padding = Math.round(3 * dpr);
    const fontSize = Math.round(10 * dpr);
    const tickMinor = Math.round(6 * dpr);
    const tickMajor = Math.round(12 * dpr);
    const clampPx = (value: number, min: number, max: number) =>
      Math.min(Math.max(value, min), max);

    const pickNiceStep = (target: number): number => {
      const t = Math.max(1, target);
      const exp = Math.floor(Math.log10(t));
      const pow10 = Math.pow(10, exp);
      const multipliers = [1, 2, 5, 10];
      for (const m of multipliers) {
        const step = m * pow10;
        if (step >= t) return Math.round(step);
      }
      return Math.round(10 * pow10);
    };

    const pickMinorStep = (major: number): number => {
      if (major >= 10 && major % 10 === 0) return major / 10;
      if (major % 5 === 0) return major / 5;
      if (major % 2 === 0) return major / 2;
      return major;
    };

    // Keep labels readable across zoom levels (target ~50 CSS px between major ticks).
    // This also increases overall ruler "precision" by adding more labeled ticks.
    const majorStep = pickNiceStep((50 * dpr) / scale);
    const minorStep = Math.max(1, Math.round(pickMinorStep(majorStep)));

    const camT = this.camera.translation;
    const canvasLeft = this.canvasTranslation.x;
    const canvasTop = this.canvasTranslation.y;
    const canvasWidth = this.canvasSize.width;
    const canvasHeight = this.canvasSize.height;

    const ctx = this.ctx;
    ctx.save();
    ctx.globalCompositeOperation = "source-over";

    // Photoshop-like rulers: fixed at viewport edges, values are relative to canvas origin
    // and can be negative outside the canvas bounds.
    const rulerThickness = Math.min(thickness, viewport.width, viewport.height);
    const tickMinorClamped = Math.min(tickMinor, Math.max(0, rulerThickness - padding));
    const tickMajorClamped = Math.min(tickMajor, Math.max(0, rulerThickness - padding));

    // Borders to separate rulers from content.
    ctx.strokeStyle = "rgba(255,255,255,0.16)";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(rulerThickness + 0.5, 0);
    ctx.lineTo(rulerThickness + 0.5, viewport.height);
    ctx.moveTo(0, rulerThickness + 0.5);
    ctx.lineTo(viewport.width, rulerThickness + 0.5);
    ctx.stroke();

    // Tick style.
    const minorTickColor = "#CCCCCC";
    const majorTickColor = "#7f7f7f";
    ctx.fillStyle = majorTickColor;
    ctx.lineWidth = 1;
    ctx.font = `${fontSize}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial`;

    // Top ruler (X axis).
    ctx.save();
    ctx.beginPath();
    ctx.rect(rulerThickness, 0, viewport.width - rulerThickness, rulerThickness);
    ctx.clip();
    ctx.textBaseline = "top";
    {
      const labelY = clampPx(
        rulerThickness - fontSize - padding,
        padding,
        rulerThickness - padding,
      );
      const globalBarLeft = camT.x + rulerThickness / scale;
      const globalBarRight = camT.x + viewport.width / scale;
      const visibleCoordStartX = globalBarLeft - canvasLeft;
      const visibleCoordEndX = globalBarRight - canvasLeft;
      const startTick = Math.floor(visibleCoordStartX / minorStep) * minorStep;

      for (let x = startTick; x <= visibleCoordEndX; x += minorStep) {
        const screenX = Math.round((canvasLeft + x - camT.x) * scale);
        if (screenX < rulerThickness || screenX > viewport.width) continue;

        const isMajor = x % majorStep === 0;
        const len = isMajor ? tickMajorClamped : tickMinorClamped;

        ctx.strokeStyle = isMajor ? majorTickColor : minorTickColor;
        ctx.beginPath();
        // Inverted ticks: from top edge -> downwards.
        ctx.moveTo(screenX + 0.5, 0);
        ctx.lineTo(screenX + 0.5, len);
        ctx.stroke();

        if (isMajor) {
          const minLabelX = rulerThickness + padding;
          const maxLabelX = viewport.width - padding;
          const labelX = clampPx(screenX, minLabelX, maxLabelX);
          ctx.textAlign = labelX <= minLabelX ? "left" : labelX >= maxLabelX ? "right" : "center";
          ctx.fillText(String(x), labelX, labelY);
        }
      }
    }
    ctx.restore();

    // Left ruler (Y axis).
    ctx.save();
    ctx.beginPath();
    ctx.rect(0, rulerThickness, rulerThickness, viewport.height - rulerThickness);
    ctx.clip();
    ctx.textBaseline = "middle";
    {
      const globalBarTop = camT.y + rulerThickness / scale;
      const globalBarBottom = camT.y + viewport.height / scale;
      const visibleCoordStartY = globalBarTop - canvasTop;
      const visibleCoordEndY = globalBarBottom - canvasTop;
      const startTick = Math.floor(visibleCoordStartY / minorStep) * minorStep;

      for (let y = startTick; y <= visibleCoordEndY; y += minorStep) {
        const screenY = Math.round((canvasTop + y - camT.y) * scale);
        if (screenY < rulerThickness || screenY > viewport.height) continue;

        const isMajor = y % majorStep === 0;
        const len = isMajor ? tickMajorClamped : tickMinorClamped;

        ctx.strokeStyle = isMajor ? majorTickColor : minorTickColor;
        ctx.beginPath();
        // Inverted ticks: from left edge -> rightwards.
        ctx.moveTo(0, screenY + 0.5);
        ctx.lineTo(len, screenY + 0.5);
        ctx.stroke();

        if (isMajor) {
          const minLabelY = rulerThickness + padding;
          const maxLabelY = viewport.height - padding;
          const labelY = clampPx(screenY, minLabelY, maxLabelY);
          const labelX = rulerThickness - padding - Math.round(fontSize / 2);

          ctx.save();
          ctx.translate(labelX, labelY);
          ctx.rotate(-Math.PI / 2);
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          ctx.fillText(String(y), 0, 0);
          ctx.restore();
        }
      }
    }
    ctx.restore();

    // Hover reference lines (ruler markers + crosshair in content).
    const hover = this._rulersHoverScreenPos;
    if (hover) {
      const mx = Math.round(hover.x) + 0.5;
      const my = Math.round(hover.y) + 0.5;

      const guideStrong = "rgba(127,127,127,0.9)";
      const guideSoft = "rgba(127,127,127,0.35)";

      ctx.lineWidth = 1;

      // Vertical guides.
      if (mx >= rulerThickness && mx <= viewport.width) {
        // Marker inside top ruler.
        ctx.strokeStyle = guideStrong;
        ctx.beginPath();
        ctx.moveTo(mx, 0);
        ctx.lineTo(mx, rulerThickness);
        ctx.stroke();

        // Line in content area.
        ctx.strokeStyle = guideSoft;
        ctx.beginPath();
        ctx.moveTo(mx, rulerThickness);
        ctx.lineTo(mx, viewport.height);
        ctx.stroke();
      }

      // Horizontal guides.
      if (my >= rulerThickness && my <= viewport.height) {
        // Marker inside left ruler.
        ctx.strokeStyle = guideStrong;
        ctx.beginPath();
        ctx.moveTo(0, my);
        ctx.lineTo(rulerThickness, my);
        ctx.stroke();

        // Line in content area.
        ctx.strokeStyle = guideSoft;
        ctx.beginPath();
        ctx.moveTo(rulerThickness, my);
        ctx.lineTo(viewport.width, my);
        ctx.stroke();
      }
    }

    ctx.restore();
  }

  private _observeComponent(component: Webgl2Component<any, any>): boolean {
    if (this._disposeMap.has(component)) return false;
    const dispose = deepObserve(component, this.render);
    this._disposeMap.set(component, dispose);
    return true;
  }

  private _disposeComponent(component: Webgl2Component<any, any>): boolean {
    const dispose = this._disposeMap.get(component);
    if (!dispose) return false;
    dispose();
    this._disposeMap.delete(component);
    return true;
  }

  private _calcScissors() {
    const cameraTopLeft = this.camera.convertGlobalToTopLeft(this.canvasTranslation).round();
    const viewSize = this.canvasSize.clone().multiply(this.camera.scale);

    this._scissorsOffset.set(
      cameraTopLeft.x,
      this.camera.projection.height - cameraTopLeft.height - viewSize.height,
    );
    this._scissorsSize.set(viewSize);
  }

  private _prepareComponents() {
    this.chessGridComponent = new ChessGridComponent();
    this.chessGridComponent.style.pointerEvents = "none";

    this._lineGridComponent = new LineGridComponent();
    this.selectionComponent = new SelectionComponent(this);
    this.canvasResizeSelectionComponent = new CanvasResizeSelectionComponent(this);
    this.cropSelectionComponent = new CropSelectionComponent(this);
    this.measurementSelectionComponent = new MeasurementSelectionComponent(this);
  }

  private _prepareTools() {
    this.tools.canvasResizeTool = new CanvasResizeTool(this);
    this.tools.cropTool = new CropTool(this);
    this.tools.eraseTool = new EraseTool(this);
    this.tools.handSelectionTool = new HandSelectionTool(this);
    this.tools.handTool = new HandTool(this);
    this.tools.measurementTool = new MeasurementTool(this);
    this.tools.retouchTool = new RetouchTool(this);
    this.tools.selectionTool = new SelectionTool(this);
  }

  private _getRulerThicknessPx(viewport: Vec2) {
    if (!this._rulersEnabled) return 0;
    const dpr = window.devicePixelRatio || 1;
    const thickness = Math.round(28 * dpr);
    return Math.min(thickness, viewport.width, viewport.height);
  }

  public recenterViewport() {
    if (this.canvasSize.getFloor().hasZero()) return;

    const viewport = this.camera.projection;
    const rulerThickness = this._getRulerThicknessPx(viewport);
    if (!rulerThickness) {
      this.camera.centerViewportOnGlobalPosition(this.canvasGlobalCenter);
      return;
    }

    const scale = this.camera.scale || 1;
    const contentViewport = viewport.clone().sub(rulerThickness, rulerThickness);
    if (contentViewport.getFloor().hasZero()) return;

    // Keep X centered within the content viewport, but align Y to the top.
    // This significantly reduces the "too wide" top spacing in portrait canvases.
    const canvasScreenW = this.canvasSize.width * scale;
    const extraX = Math.max(0, (contentViewport.width - canvasScreenW) / 2);

    const desiredLeftPx = rulerThickness + extraX;
    const desiredTopPx = rulerThickness + 8;

    this.camera.setTranslation(
      this.canvasTranslation.x - desiredLeftPx / scale,
      this.canvasTranslation.y - desiredTopPx / scale,
    );
  }
}

export default Webgl2dRenderer;
